// Step 3: Personel Seçimi ve Belge Düzenleme (Birleşik Tablo Görünümü)
import { permitEmployeeDocKeys } from './wizardHelpers';
import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import { Textarea } from '../../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../ui/dialog';
import { toast } from 'sonner';
import { IdCardChip } from '../AdvancedQr';
import { Users, Plus, Search, Check, Clock, AlertTriangle, X, ChevronDown, ChevronUp, MinusCircle, GraduationCap, Eye, Paperclip, FileSpreadsheet, Download, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import { EMPLOYEE_DOCS, DOC_STATUS_OPTIONS } from '../../employees/constants';
import { DocumentViewerDialog } from '../../employees/DocumentViewerDialog';
import { MaskFixRequestButton } from '../../health-mask/MaskFixRequestButton';
import { FirmDocFileActions, FirmDocStatusBadge, firmDocStatus } from '../../documents/FirmDocFileActions';
import { FirmDocCell, DeficiencyList, NewVersionUpload } from '../../documents/FirmDocCell';
import { DocumentUploadButton } from '../../employees/DocumentUploadButton';
import { DocReviewPanel } from '../../documents/DocReviewPanel';
import { SicilMatchBox, sicilBlockReason } from '../../documents/SicilMatchBox';
import { SGK_WARNING, SgkMarkIcon, buildSgkData, sgkMark, sgkNeedsWarning } from './sgkCheck';
import { EmployeePoolDialog } from './EmployeePoolDialog';
import { blocksEmployeeDoc } from './BulkUploadDialog';
import { MatrixUploadDialog } from './MatrixUploadDialog';
import { PageCountBadge } from '../../documents/PageCountBadge';
import { TablePagination, paginate } from './TablePagination';
import { exportEmployeesToExcel } from './exportEmployees';
import { useTcSearchIds, withFullTc } from '../../../lib/tcPrivacy';
import { ExcelImportDialog } from './ExcelImportDialog';
import { useAuth } from '../../../context/AuthContext';
import { calculateOverallStatus, maskTcNo, formatDate, calculateValidityDate, getValidityPeriodText, validityBlockMessage } from '../../../lib/helpers';
import { PageLimitTag } from '../../documents/PageLimitTag';
import { isForeign, PassportTag } from '../../employees/Nationality';
import { useFitZoom } from '../../../hooks/useFitZoom';

// Düzeltme ekranında bir kez eksik olarak listelenen personel belgeleri (sekme oturumu boyunca, yenilemede de korunur)
const FIX_DOCS_KEY = 'fix-mode-shown-docs';
const shownFixDocs = new Set(JSON.parse(sessionStorage.getItem(FIX_DOCS_KEY) || '[]'));
const rememberFixDoc = (id) => { if (!shownFixDocs.has(id)) { shownFixDocs.add(id); sessionStorage.setItem(FIX_DOCS_KEY, JSON.stringify([...shownFixDocs])); } };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kısa belge isimleri
const DOC_SHORT_NAMES = {
  'sgk_ise_giris': 'Bildirge',
  'isg_egitimi': 'Eğitim',
  'saglik_raporu': 'Sağlık',
  'mesleki_yeterlilik': 'MYK',
  'mesleki_egitim': 'ME',
  'kkd_zimmet': 'KKD'
};

// Durum renkleri ve ikonları
const STATUS_CONFIG = {
  'onay': { bg: 'bg-emerald-100', text: 'text-emerald-700', icon: Check, hoverBg: 'hover:bg-emerald-200' },
  'eksik': { bg: 'bg-red-100', text: 'text-red-700', icon: X, hoverBg: 'hover:bg-red-200' },
  'beklemede': { bg: 'bg-amber-100', text: 'text-amber-700', icon: Clock, hoverBg: 'hover:bg-amber-200' },
  'suresi_doldu': { bg: 'bg-orange-100', text: 'text-orange-700', icon: AlertTriangle, hoverBg: 'hover:bg-orange-200' },
  'kapsam_disi': { bg: 'bg-slate-100', text: 'text-slate-500', icon: MinusCircle, hoverBg: 'hover:bg-slate-200' },
};

const WizardStep3Combined = ({
  employees,
  setEmployees,
  selectedEmployees,
  setSelectedEmployees,
  selectedWorkArea,
  selectedCompany,
  formData,
  setNewEmployeeDialog,
  handleEmployeeSelect,
  onEmployeesRefresh,
  templates = [],
  mykList = [],
  fixMode = false,
  reportedFixDocs = [],
  blockSignal = 0,
  permitId,
  setFormData,
  isCopiedPermit = false,
  sectionCompanyId,
  sectionKey = 'main'
}) => {
  const { isFirm, isReviewer, canCreate } = useAuth();
  // Eksik/Hatalı talep düzeltme: onaylı belgeler ve tamamen onaylı personeller varsayılan gizli
  const [showAllDocs, setShowAllDocs] = useState(false);
  const [poolOpen, setPoolOpen] = useState(false);
  const hideApproved = fixMode && !showAllDocs;
  // İSG: varsayılan sadece onay bekleyen veya eksik belgeler; buton ile tüm belgeler
  const reviewerHide = isReviewer && !showAllDocs;
  const tableMinW = 'min-w-fit';
  const [employeeSearch, setEmployeeSearch] = useState('');
  const tcSearchIds = useTcSearchIds(employeeSearch);
  const [employeeStatusFilter, setEmployeeStatusFilter] = useState('all');
  const [showDocRequirements, setShowDocRequirements] = useState(false);
  const [openPopover, setOpenPopover] = useState(null);
  const [tempDate, setTempDate] = useState('');
  const [viewerDoc, setViewerDoc] = useState(null);
  
  // Eksiklik şablonları için state
  const [showDeficiencyDialog, setShowDeficiencyDialog] = useState(false);
  const [selectedEmployeeForDoc, setSelectedEmployeeForDoc] = useState(null);
  const [selectedDocKey, setSelectedDocKey] = useState(null);
  const [selectedDeficiencies, setSelectedDeficiencies] = useState([]);
  
  // MYK seçimi için state
  const [showMykDialog, setShowMykDialog] = useState(false);
  const [mykSelectorEmployee, setMykSelectorEmployee] = useState(null);
  const [mykSelectorStatus, setMykSelectorStatus] = useState(null);
  const [selectedMykObj, setSelectedMykObj] = useState(null);
  const [mykSearchQuery, setMykSearchQuery] = useState('');
  const [mykValidityDate, setMykValidityDate] = useState('');
  const [mykRejectNote, setMykRejectNote] = useState('');
  const [showDiplomaInput, setShowDiplomaInput] = useState(false);
  const [diplomaText, setDiplomaText] = useState('');
  
  const closeUi = () => { setOpenPopover(null); setViewerDoc(null); };
  const requiredDocs = selectedWorkArea?.required_employee_docs || [];
  const optionalDocs = selectedWorkArea?.optional_employee_docs || [];
  const docKeys = EMPLOYEE_DOCS.map(d => d.key);
  const sgkData = buildSgkData(formData, selectedCompany);
  // Kopyalanan talep: firmanın SGK listesinde kontrol edilmemiş personel eklendiyse firmaya uyarı
  const sgkReviewedIds = selectedCompany?.documents?.sgk_hizmet_listesi_reviewed_ids || [];
  const sgkNewEmps = isFirm && isCopiedPermit && selectedCompany?.documents?.sgk_hizmet_listesi === 'onay'
    ? selectedEmployees.filter(e => !sgkReviewedIds.includes(e.id)) : [];
  const sgkNewCount = useRef(sgkNewEmps.length);
  useEffect(() => {
    if (sgkNewEmps.length > sgkNewCount.current) toast.warning('SGK Hizmet Listesi yeniden İSG kontrolüne sunulacak: yeni eklenen personel listede kontrol edilecek.', { duration: 7000 });
    sgkNewCount.current = sgkNewEmps.length;
  }, [sgkNewEmps.length]);
  const empDocKeys = (emp) => permitEmployeeDocKeys(emp, selectedWorkArea, docKeys, formData.selected_optional_employee_docs || []);
  const [bulkEmp, setBulkEmp] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  // İSG toplu kontrol: onay bekleyen personel belgeleri sırayla açılır; karar verilince sıradaki gelir
  const [reviewQueue, setReviewQueue] = useState(null);
  const pendingReview = employees.filter(e => selectedEmployees.some(x => x.id === e.id))
    .flatMap(e => EMPLOYEE_DOCS.filter(d => (e.documents?.[d.key] || 'beklemede') === 'beklemede' && e.document_files?.[d.key]).map(d => ({ id: `${e.id}:${d.key}`, e, d })));
  const openQueueItem = (id) => {
    const [empId, key] = id.split(':');
    const e = employees.find(x => x.id === empId);
    const d = EMPLOYEE_DOCS.find(x => x.key === key);
    setViewerDoc({ employee: e, docKey: key, label: d.label });
  };
  // Karar verilip pencere kapanınca (veya ilk açılışta) sıradaki belgeye geç
  useEffect(() => {
    if (!reviewQueue || viewerDoc || showMykDialog || showDeficiencyDialog) return;
    const idx = reviewQueue.opened ? reviewQueue.idx + 1 : reviewQueue.idx;
    if (idx >= reviewQueue.list.length) { setReviewQueue(null); toast.success('Toplu kontrol tamamlandı'); return; }
    setReviewQueue(q => ({ ...q, idx, opened: true }));
    openQueueItem(reviewQueue.list[idx]);
  }, [reviewQueue, viewerDoc, showMykDialog, showDeficiencyDialog]); // eslint-disable-line react-hooks/exhaustive-deps
  const goQueue = (idx) => { setReviewQueue(q => ({ ...q, idx, opened: true })); openQueueItem(reviewQueue.list[idx]); };
  const openBulk = (target) => setBulkEmp(target);
  const [excelOpen, setExcelOpen] = useState(false);
  // İSG: personel tablosunda kaldığı sayfa hatırlanır
  // Ekipman firması bölümünde sicil/İSG adları o firmanın kendi kaydından gelir
  const advancedQr = false; // Kimlik (QR) yükleme çalışma izni sihirbazından kaldırıldı
  const patchEmployee = (id, patch) => { const fn = (x) => (x.id === id ? { ...x, ...patch } : x); setEmployees(prev => prev.map(fn)); setSelectedEmployees(prev => prev.map(fn)); };
  const firmRef = sectionKey === 'eq' ? (selectedCompany?.documents || {}) : formData;
  const pageKey = isReviewer && permitId ? `wizard-emp-page-${permitId}${sectionKey === "eq" ? "-eq" : ""}` : null;
  const [page, setPage] = useState(() => (pageKey && Number(JSON.parse(localStorage.getItem(pageKey) || '{}').page)) || 1);
  const [pageSize, setPageSize] = useState(() => (pageKey && Number(JSON.parse(localStorage.getItem(pageKey) || '{}').size)) || 10);
  const restoredKey = useRef(pageKey);
  useEffect(() => {
    if (!pageKey || restoredKey.current === pageKey) return;
    restoredKey.current = pageKey;
    const saved = JSON.parse(localStorage.getItem(pageKey) || '{}');
    if (saved.page) setPage(Number(saved.page));
    if (saved.size) setPageSize(Number(saved.size));
  }, [pageKey]);
  useEffect(() => { if (pageKey) localStorage.setItem(pageKey, JSON.stringify({ page, size: pageSize })); }, [pageKey, page, pageSize]);
  const [showBlocked, setShowBlocked] = useState(false);
  const [pendingJump, setPendingJump] = useState(false);
  // Excel ile eklenenler: liste yenilenir, yeni ve zaten kayıtlı personeller talebe eklenir
  const handleExcelDone = async (created, existingIds) => {
    await onEmployeesRefresh?.();
    created.forEach(emp => handleEmployeeSelect(emp, true));
    employees.filter(e => existingIds.includes(e.id) && !selectedEmployees.some(s => s.id === e.id)).forEach(emp => handleEmployeeSelect(emp, true));
  };
  const otherDocs = docKeys.filter(k => !requiredDocs.includes(k) && !optionalDocs.includes(k));

  // Şablonları belge tipine göre getir
  const getTemplatesForDoc = (docKey) => {
    return templates.filter(t => t.document_type === docKey);
  };

  // MYK listesini filtrele
  const filteredMykList = mykList.filter(myk => {
    const query = mykSearchQuery.toLowerCase();
    return (
      myk.name?.toLowerCase().includes(query) ||
      myk.code?.toLowerCase().includes(query) ||
      myk.description?.toLowerCase().includes(query)
    );
  });

  // Filtreleme
  // İnceleyen personel seçemez/kaldıramaz: yalnızca talepteki personeller listelenir
  // Sadece talebe eklenen personeller listelenir; firma yeni personeli havuzdan seçer veya "Yeni Personel" ile ekler
  let filteredEmployees = employees.filter(emp => selectedEmployees.some(s => s.id === emp.id));
  
  if (employeeStatusFilter !== 'all') {
    if (employeeStatusFilter === 'suresi_dolacak') {
      // Talep aralığında belgesi dolacak personeller
      filteredEmployees = filteredEmployees.filter(emp => {
        if (!formData.end_date) return false;
        const endDate = (formData.end_date || '').slice(0, 10);
        const docsToCheck = [
          { key: 'isg_egitimi', validityField: 'isg_egitimi_validity' },
          { key: 'saglik_raporu', validityField: 'saglik_raporu_validity' },
          { key: 'mesleki_yeterlilik', validityField: 'mesleki_yeterlilik_validity', altField: 'mesleki_yeterlilik_date' },
        ];
        return docsToCheck.some(doc => {
          const status = emp.documents?.[doc.key];
          if (status === 'onay' || status === 'suresi_doldu') {
            const validityDate = emp.documents?.[doc.validityField] || (doc.altField ? emp.documents?.[doc.altField] : null);
            if (validityDate) {
              const validity = String(validityDate).slice(0, 10);
              return validity < endDate;
            }
          }
          return false;
        });
      });
    } else {
      filteredEmployees = filteredEmployees.filter(emp => {
        const overall = calculateOverallStatus(emp.documents, empDocKeys(emp));
        return overall.status === employeeStatusFilter;
      });
    }
  }
  
  // Düzeltme modunda görünenler: eksik belgeler ve yüklenmiş ama onay bekleyenler; bir kez listede görünen belge (ör. silinip "Yükle"ye dönen) listede kalır
  const needsAttention = (emp, key) => {
    const st = emp.documents?.[key];
    const id = `${emp.id}:${key}`;
    if (['eksik', 'suresi_doldu'].includes(st) || ((!st || st === 'beklemede') && !!emp.document_files?.[key])) { rememberFixDoc(id); return true; }
    return st !== 'onay' && st !== 'kapsam_disi' && (shownFixDocs.has(id) || reportedFixDocs.includes(id));
  };
  const reviewerAttention = (emp, key) => {
    const st = emp.documents?.[key];
    return ['eksik', 'suresi_doldu'].includes(st) || ((!st || st === 'beklemede') && !!emp.document_files?.[key]);
  };
  const isVisibleDoc = (emp, key) => (hideApproved ? needsAttention(emp, key) : reviewerHide ? reviewerAttention(emp, key) : true);
  if (hideApproved || reviewerHide) {
    filteredEmployees = filteredEmployees.filter(emp => EMPLOYEE_DOCS.some(d => isVisibleDoc(emp, d.key)));
  }

  // Düzeltme modunda / İSG'de sadece ilgili belgesi olan sütunlar gösterilir
  const tableDocs = hideApproved || reviewerHide
    ? EMPLOYEE_DOCS.filter(d => filteredEmployees.some(emp => isVisibleDoc(emp, d.key)))
    : EMPLOYEE_DOCS;
  const gridStyle = { gridTemplateColumns: `56px 50px 150px 120px 80px ${isReviewer ? '50px ' : ''}repeat(${tableDocs.length}, ${isFirm ? 120 : 90}px)${advancedQr ? ' 125px' : ''}${isFirm ? ' minmax(140px, 1fr)' : ''}` };
  const gridCols = [56, 50, 150, 120, 80, ...(isReviewer ? [50] : []), ...tableDocs.map(() => (isFirm ? 120 : 90)), ...(advancedQr ? [125] : []), ...(isFirm ? [140] : [])];
  const fitZoom = useFitZoom(gridCols.reduce((a, b) => a + b, 0) + (gridCols.length - 1) * 4 + 18);

  if (employeeSearch.length >= 2) {
    const searchLower = employeeSearch.toLowerCase();
    filteredEmployees = filteredEmployees.filter(emp => {
      const fullName = `${emp.name} ${emp.surname}`.toLowerCase();
      const position = emp.position?.toLowerCase() || '';
      return fullName.includes(searchLower) || !!tcSearchIds?.has(emp.id) || position.includes(searchLower);
    });
  }
  
  const blockReq = selectedWorkArea?.required_employee_docs || docKeys;
  const blockedIds = new Set(showBlocked ? filteredEmployees.filter(e => docKeys.some(k => blocksEmployeeDoc(e, k, blockReq))).map(e => e.id) : []);
  const pg = paginate(filteredEmployees, page, pageSize);
  const pageMarks = {};
  filteredEmployees.forEach((e, i) => { if (blockedIds.has(e.id)) { const p = Math.floor(i / pageSize) + 1; pageMarks[p] = (pageMarks[p] || 0) + 1; } });
  // İleri engellendiğinde: filtreleri temizle, ilk eksik personelin sayfasını aç
  useEffect(() => {
    if (!blockSignal) return;
    setEmployeeSearch(''); setEmployeeStatusFilter('all'); setShowBlocked(true); setPendingJump(true);
  }, [blockSignal]);
  useEffect(() => {
    if (!pendingJump) return;
    setPendingJump(false);
    const idx = filteredEmployees.findIndex(e => blockedIds.has(e.id));
    if (idx >= 0) setPage(Math.floor(idx / pageSize) + 1);
  }, [pendingJump]); // eslint-disable-line react-hooks/exhaustive-deps

  // NOT: Otomatik sıralama kaldırıldı - işlem sırasında liste sırası değişmesin
  // Kullanıcı isterse filtreleme ile sıralama yapabilir

  // State'i güncelle helper
  const updateEmployeeState = (employeeId, updatedDocs) => {
    const updateFn = (emp) => emp.id === employeeId ? { ...emp, documents: updatedDocs } : emp;
    setEmployees(prev => prev.map(updateFn));
    setSelectedEmployees(prev => prev.map(updateFn));
  };

  const updateEmployeeFile = (employeeId, docKey, fileInfo) => {
    const updateFn = (emp) => emp.id === employeeId ? { ...emp, documents: { ...(emp.documents || {}), [docKey]: 'beklemede' }, document_files: { ...(emp.document_files || {}), [docKey]: fileInfo } } : emp;
    setEmployees(prev => prev.map(updateFn));
    setSelectedEmployees(prev => prev.map(updateFn));
  };

  const removeEmployeeFile = (employeeId, docKey, res) => {
    rememberFixDoc(`${employeeId}:${docKey}`);
    const updateFn = (emp) => {
      if (emp.id !== employeeId) return emp;
      const files = { ...(emp.document_files || {}) };
      if (res.file) files[docKey] = res.file; else delete files[docKey];
      return { ...emp, documents: { ...(emp.documents || {}), [docKey]: res.status }, document_files: files };
    };
    setEmployees(prev => prev.map(updateFn));
    setSelectedEmployees(prev => prev.map(updateFn));
  };

  // ==================== İŞ KURALLARI ====================

  // Tarih doğrulama - İSG ve Sağlık tarihleri SGK tarihinden sonra olmalı
  const validateDates = (employee, docKey, newDate) => {
    const docs = employee.documents || {};
    const sgkDate = docs.sgk_ise_giris_date;
    
    if ((docKey === 'isg_egitimi' || docKey === 'saglik_raporu') && sgkDate && newDate) {
      if (new Date(newDate) < new Date(sgkDate)) {
        const docLabel = docKey === 'isg_egitimi' ? 'İSG Eğitimi' : 'Sağlık Raporu';
        return {
          isValid: false,
          message: `${docLabel} tarihi, SGK işe giriş tarihinden (${formatDate(sgkDate)}) önce olamaz!`
        };
      }
    }
    return { isValid: true, message: null };
  };

  // SGK İşe Giriş eksik yapıldığında İSG ve Sağlık'ı beklemede yap (Cascade Rule)
  const applyCascadeRuleForSgkEksik = (docs) => {
    return {
      ...docs,
      sgk_ise_giris: 'eksik',
      // İSG ve Sağlık'ı beklemede yap
      isg_egitimi: 'beklemede',
      isg_egitimi_note: '',
      isg_egitimi_date: null,
      isg_egitimi_validity: null,
      saglik_raporu: 'beklemede',
      saglik_raporu_note: '',
      saglik_raporu_date: null,
      saglik_raporu_validity: null
    };
  };

  // SGK İşe Giriş onaylandığında ilgili belgeleri kontrol et
  const checkAndUpdateRelatedDocs = (employee, newSgkDate) => {
    const docs = employee.documents || {};
    let warnings = [];
    const updatedDocs = { ...docs, sgk_ise_giris_date: newSgkDate };
    
    // İSG Eğitimi kontrolü
    const isgDate = docs.isg_egitimi_date;
    if (isgDate && docs.isg_egitimi === 'onay' && new Date(isgDate) < new Date(newSgkDate)) {
      updatedDocs.isg_egitimi = 'beklemede';
      warnings.push(`İSG Eğitimi tarihi (${formatDate(isgDate)}) SGK giriş tarihinden önce olduğu için "Beklemede" yapıldı.`);
    }
    
    // Sağlık Raporu kontrolü
    const saglikDate = docs.saglik_raporu_date;
    if (saglikDate && docs.saglik_raporu === 'onay' && new Date(saglikDate) < new Date(newSgkDate)) {
      updatedDocs.saglik_raporu = 'beklemede';
      warnings.push(`Sağlık Raporu tarihi (${formatDate(saglikDate)}) SGK giriş tarihinden önce olduğu için "Beklemede" yapıldı.`);
    }
    
    return { updatedDocs, warnings };
  };

  // ==================== BELGE GÜNCELLEME ====================

  // Ana belge durumu güncelleme fonksiyonu
  const handleDocStatusChange = async (employee, docKey, newStatus, docConfig, dateOverride) => {
    const docs = employee.documents || {};
    const sgkDate = docs.sgk_ise_giris_date;
    
    // SGK işe giriş tarihi sadece İSG Eğitimi ve Sağlık Raporu için gerekir (kapsam dışı hariç)
    if (['isg_egitimi', 'saglik_raporu'].includes(docKey) && newStatus !== 'kapsam_disi' && !sgkDate) {
      toast.error('Önce SGK İşe Giriş tarihini girmelisiniz!');
      closeUi();
      return;
    }

    // ONAY durumu
    if (newStatus === 'onay') {
      if (!employee.document_files?.[docKey]) {
        toast.error('Onay için önce belge dosyası yüklenmeli ve görüntülenmelidir');
        return;
      }
      // Tarih gerekli belgeler için kontrol
      const dateToSave = dateOverride || tempDate || docs[`${docKey}_date`];
      
      if (docConfig.hasDate && !dateToSave) {
        toast.error('Onay vermek için önce tarih girmelisiniz!');
        return;
      }
      
      // SGK İşe Giriş onayı - ilgili belgeleri kontrol et
      if (docKey === 'sgk_ise_giris') {
        const { updatedDocs, warnings } = checkAndUpdateRelatedDocs(employee, dateToSave);
        updatedDocs.sgk_ise_giris = 'onay';
        updatedDocs.sgk_ise_giris_note = '';
        updatedDocs.sgk_ise_giris_date = dateToSave;
        const sicilVal = sicilDraft.key === employee.id ? sicilDraft.value : '';
        const sicilErr = sicilBlockReason(firmRef.sgk_sicil_last4, sicilVal);
        if (sicilErr) { toast.error(sicilErr); return; }
        updatedDocs.sgk_ise_giris_sicil_last4 = sicilVal;
        const sgkWarn = sgkNeedsWarning(sgkData, employee.id, dateToSave);
        if (sgkWarn && !window.confirm(`${employee.name} ${employee.surname}: ${SGK_WARNING}\n\nYine de onaylamak istiyor musunuz?`)) return;
        
        try {
          await axios.patch(`${API}/employees/${employee.id}/documents`, { documents: updatedDocs });
          updateEmployeeState(employee.id, updatedDocs);
          if (sgkWarn) await ackSgkWarning(employee.id);
          
          if (warnings.length > 0) {
            toast.warning(warnings.join('\n'));
          } else {
            toast.success('Belge onaylandı');
          }
          closeUi();
          setTempDate('');
        } catch (error) {
          toast.error('Güncelleme başarısız');
        }
        return;
      }
      
      // İSG ve Sağlık için tarih doğrulama
      if (docKey === 'isg_egitimi' || docKey === 'saglik_raporu') {
        const validation = validateDates(employee, docKey, dateToSave);
        if (!validation.isValid) {
          toast.error(validation.message);
          return;
        }
      }
      const vErr = docConfig.hasValidity ? validityError(calcValidity(docKey, dateToSave)) : null;
      if (vErr) { toast.error(vErr); return; }
      
      // Normal onay
      try {
        const updatedDocs = {
          ...docs,
          [docKey]: 'onay',
          [`${docKey}_note`]: '',
          [`${docKey}_date`]: dateToSave
        };
        
        // İSG Eğitimi ve Sağlık Raporu için tehlike sınıfına göre otomatik validity hesapla
        if ((docKey === 'isg_egitimi' || docKey === 'saglik_raporu') && dateToSave && selectedCompany?.hazard_class) {
          const validityDate = calculateValidityDate(docKey, selectedCompany.hazard_class, dateToSave);
          if (validityDate) {
            updatedDocs[`${docKey}_validity`] = validityDate;
          }
        }
        
        await axios.patch(`${API}/employees/${employee.id}/documents`, { documents: updatedDocs });
        updateEmployeeState(employee.id, updatedDocs);
        
        // Geçerlilik tarihi varsa göster
        if (updatedDocs[`${docKey}_validity`]) {
          toast.success(`Belge onaylandı - Geçerlilik: ${formatDate(updatedDocs[`${docKey}_validity`])}`);
        } else {
          toast.success('Belge onaylandı');
        }
        closeUi();
        setTempDate('');
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    }
    // EKSİK durumu
    else if (newStatus === 'eksik') {
      // SGK İşe Giriş eksik yapılırsa cascade rule uygula
      if (docKey === 'sgk_ise_giris') {
        try {
          const updatedDocs = applyCascadeRuleForSgkEksik(docs);
          await axios.patch(`${API}/employees/${employee.id}/documents`, { documents: updatedDocs });
          updateEmployeeState(employee.id, updatedDocs);
          toast.warning('SGK İşe Giriş eksik yapıldı. İSG Eğitimi ve Sağlık Raporu beklemede olarak ayarlandı.');
          closeUi();
        } catch (error) {
          toast.error('Güncelleme başarısız');
        }
        return;
      }
      
      // Eksiklik şablonları varsa dialog aç
      const docTemplates = getTemplatesForDoc(docKey);
      if (docTemplates.length > 0) {
        setSelectedEmployeeForDoc(employee);
        setSelectedDocKey(docKey);
        setSelectedDeficiencies([]);
        setShowDeficiencyDialog(true);
        closeUi();
      } else {
        // Şablon yoksa direkt eksik yap
        try {
          const updatedDocs = {
            ...docs,
            [docKey]: 'eksik',
            [`${docKey}_note`]: '',
            [`${docKey}_date`]: null,
            [`${docKey}_validity`]: null
          };
          await axios.patch(`${API}/employees/${employee.id}/documents`, { documents: updatedDocs });
          updateEmployeeState(employee.id, updatedDocs);
          toast.success('Belge eksik olarak işaretlendi');
          closeUi();
        } catch (error) {
          toast.error('Güncelleme başarısız');
        }
      }
    }
    // BEKLEMEDE durumu
    else if (newStatus === 'beklemede') {
      try {
        const updatedDocs = {
          ...docs,
          [docKey]: 'beklemede',
          [`${docKey}_note`]: ''
        };
        await axios.patch(`${API}/employees/${employee.id}/documents`, { documents: updatedDocs });
        updateEmployeeState(employee.id, updatedDocs);
        toast.success('Belge durumu güncellendi');
        closeUi();
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    }
    // KAPSAM DIŞI durumu
    else if (newStatus === 'kapsam_disi') {
      // Yüklü dosya silinmez; sadece durum değişir
      try {
        const updatedDocs = {
          ...docs,
          [docKey]: 'kapsam_disi',
          [`${docKey}_note`]: ''
        };
        await axios.patch(`${API}/employees/${employee.id}/documents`, { documents: updatedDocs });
        updateEmployeeState(employee.id, updatedDocs);
        toast.success('Belge kapsam dışı olarak işaretlendi');
        closeUi();
      } catch (error) {
        toast.error('Güncelleme başarısız');
      }
    }
  };

  // Önizleme penceresinden red (eksik) kaydı
  const handleDocReject = async (employee, docKey, { deficiencies, reason }) => {
    const docs = employee.documents || {};
    const updatedDocs = docKey === 'sgk_ise_giris'
      ? { ...applyCascadeRuleForSgkEksik(docs), sgk_ise_giris_note: [...deficiencies, reason].filter(Boolean).join(', ') }
      : { ...docs, [docKey]: 'eksik', [`${docKey}_note`]: [...deficiencies, reason].filter(Boolean).join(', '), [`${docKey}_date`]: null, [`${docKey}_validity`]: null };
    try {
      await axios.patch(`${API}/employees/${employee.id}/documents`, { documents: updatedDocs });
      updateEmployeeState(employee.id, updatedDocs);
      toast.success(docKey === 'sgk_ise_giris' ? 'SGK İşe Giriş reddedildi. İSG Eğitimi ve Sağlık Raporu beklemede olarak ayarlandı.' : 'Belge reddedildi');
      closeUi();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Önizleme penceresindeki onay/red paneli
  // İSG Eğitimi / Sağlık Raporu görüntülenirken ilgili uzman / hekim adı
  const viewerDocs = viewerDoc ? ((employees.find(e => e.id === viewerDoc.employee.id) || viewerDoc.employee).documents || {}) : {};
  const viewerValidity = viewerDoc && (viewerDoc.docKey === 'mesleki_yeterlilik'
    ? viewerDocs.mesleki_yeterlilik_validity || viewerDocs.mesleki_yeterlilik_date
    : viewerDocs[`${viewerDoc.docKey}_validity`]);
  // Takvimden seçilen (henüz kaydedilmemiş) tarih; üstteki kutuda anında gösterilir ve geçerlilik kontrol edilir
  const [draftDate, setDraftDate] = useState({ key: '', date: '' });
  const [sicilDraft, setSicilDraft] = useState({ key: '', value: '' });
  const viewerKey = viewerDoc ? `${viewerDoc.employee.id}-${viewerDoc.docKey}` : '';
  const pickedDate = draftDate.key === viewerKey ? draftDate.date : '';
  const viewerDocDef = viewerDoc && EMPLOYEE_DOCS.find(d => d.key === viewerDoc.docKey);
  const calcValidity = (docKey, date) => {
    if (!date) return null;
    if (docKey === 'mesleki_yeterlilik') return date;
    return selectedCompany?.hazard_class ? calculateValidityDate(docKey, selectedCompany.hazard_class, date) : null;
  };
  const pickedValidity = viewerDocDef?.hasValidity ? calcValidity(viewerDoc.docKey, pickedDate) : null;
  const validityError = (validity) => validityBlockMessage(validity, formData.end_date);
  // İSG Eğitimi: uzman + hekim; Sağlık Raporu: hekim
  const uzmanRow = ['İSG Katip Uzman', firmRef.isg_katip_uzman_name];
  const hekimRow = ['İşyeri Hekimi', firmRef.isg_katip_hekim_name];
  const viewerPersons = viewerDoc && { isg_egitimi: [uzmanRow, hekimRow], saglik_raporu: [hekimRow] }[viewerDoc.docKey];

  const ackSgkWarning = async (empId) => {
    try {
      const res = await axios.put(`${API}/work-permits/${permitId}/sgk-check`, { ack_employee_id: empId });
      setFormData(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      toast.error('SGK uyarı onayı kaydedilemedi');
    }
  };

  const saveEmpSicil = async (emp, value) => {
    const updatedDocs = { ...(emp.documents || {}), sgk_ise_giris_sicil_last4: value };
    try {
      await axios.patch(`${API}/employees/${emp.id}/documents`, { documents: { sgk_ise_giris_sicil_last4: value } });
      updateEmployeeState(emp.id, updatedDocs);
      toast[value === firmRef.sgk_sicil_last4 ? 'success' : 'error'](value === firmRef.sgk_sicil_last4 ? 'Sicil kaydedildi: eşleşti' : 'Sicil kaydedildi: EŞLEŞMİYOR');
    } catch (error) {
      toast.error('Sicil kaydedilemedi');
    }
  };

  const renderEmployeeDocPanel = () => {
    const emp = employees.find(e => e.id === viewerDoc.employee.id) || viewerDoc.employee;
    const doc = EMPLOYEE_DOCS.find(d => d.key === viewerDoc.docKey);
    const docs = emp.documents || {};
    const sgkDate = docs.sgk_ise_giris_date;
    const needsMinDate = (doc.key === 'isg_egitimi' || doc.key === 'saglik_raporu') && sgkDate;
    // Mesleki Yeterlilik (MYK listesi varsa): onayda MYK seçimi açılır; Eksik/Hatalı şablonlarla bu panelde yapılır
    const mykMode = doc.key === 'mesleki_yeterlilik' && mykList.length > 0;
    const isSgk = doc.key === 'sgk_ise_giris';
    const approvedDoc = docs[doc.key] === 'onay';
    const sicilVal = sicilDraft.key === emp.id ? sicilDraft.value : '';
    const sicilErr = isSgk && !approvedDoc ? sicilBlockReason(firmRef.sgk_sicil_last4, sicilVal) : null;
    const sgkDateNow = approvedDoc ? docs.sgk_ise_giris_date : (draftDate.key === `${emp.id}-${doc.key}` ? draftDate.date : docs.sgk_ise_giris_date);
    const sgkWarnNow = isSgk && sgkNeedsWarning(sgkData, emp.id, sgkDateNow);
    const iseGirisBlock = ['isg_egitimi', 'saglik_raporu'].includes(doc.key) && docs.sgk_ise_giris === 'eksik' ? 'SGK İşe Giriş eksik/hatalı olduğu için bu belge onaylanamaz' : null;
    return (
      <>
      {docs[`${doc.key}_stale`] && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs font-medium text-amber-800" data-testid="doc-stale-note">
          Bağlı olduğu belge (SGK Hizmet Listesi / İSG Katip / SGK İşe Giriş) değiştiği için bu belge yeniden onay bekliyor. Lütfen tekrar kontrol edin.
        </div>
      )}
      {isSgk && isReviewer && sgkWarnNow && (
        <div className="space-y-2 rounded-lg border-2 border-rose-400 bg-rose-50 p-3 text-xs font-semibold text-rose-700" data-testid="sgk-list-warning">
          <p>{SGK_WARNING}</p>
          {approvedDoc && !(formData.sgk_warning_ack_ids || []).includes(emp.id) && (
            <button type="button" onClick={() => ackSgkWarning(emp.id)} className="rounded-md border border-rose-300 bg-white px-2 py-1 text-rose-700 hover:bg-rose-100" data-testid="sgk-list-warning-ack">Uyarıyı gördüm, onaylı kalsın</button>
          )}
        </div>
      )}
      {isSgk && (
        <SicilMatchBox key={`sicil-${emp.id}`} refSicil={firmRef.sgk_sicil_last4 || ''} savedSicil={docs.sgk_ise_giris_sicil_last4 || ''} approved={approvedDoc}
          value={sicilVal} onChange={(v) => setSicilDraft({ key: emp.id, value: v })} onSave={(v) => saveEmpSicil(emp, v)} />
      )}
      {doc.key === 'saglik_raporu' && !approvedDoc && emp.document_files?.saglik_raporu && (
        <MaskFixRequestButton employee={emp} onDone={(res) => { updateEmployeeState(emp.id, res.documents); closeUi(); }} />
      )}
      <DocReviewPanel
        key={`${emp.id}-${doc.key}`}
        approved={docs[doc.key] === 'onay'}
        docLabel={doc.label}
        initialDate={docs[`${doc.key}_date`] || ''}
        dateLabel={doc.hasDate && !mykMode ? 'Tarih' : null}
        dateHint={needsMinDate ? `İşe giriş tarihi ${formatDate(sgkDate)} olduğu için ${doc.label} tarihi bu tarihten önce olamaz.` : null}
        dateRequired={doc.hasDate && !mykMode}
        onDateChange={(d) => setDraftDate({ key: `${emp.id}-${doc.key}`, date: d })}
        dateError={doc.hasValidity && !mykMode ? validityError(calcValidity(doc.key, draftDate.key === `${emp.id}-${doc.key}` ? draftDate.date : docs[`${doc.key}_date`])) : null}
        minDate={needsMinDate ? sgkDate : undefined}
        templates={getTemplatesForDoc(doc.key).map(t => t.template_text)}
        approveHint={emp.is_blocked ? 'Personel bloklanmıştır, onay verilemez' : iseGirisBlock || (sicilErr ? sicilErr : mykMode ? 'Onay için MYK belgesi seçilecek' : (doc.key === 'isg_egitimi' || doc.key === 'saglik_raporu') && selectedCompany?.hazard_class ? `Geçerlilik: ${getValidityPeriodText(doc.key, selectedCompany.hazard_class)}` : null)}
        onApprove={(date) => (mykMode ? (setViewerDoc(null), openMykSelector(emp, 'onay')) : handleDocStatusChange(emp, doc.key, 'onay', doc, date))}
        onReject={(data) => handleDocReject(emp, doc.key, data)}
        approveDisabled={!!emp.is_blocked || !!sicilErr || !!iseGirisBlock}
        onRejectClick={mykMode ? () => { setViewerDoc(null); openMykSelector(emp, 'eksik'); } : undefined}
        onNotApplicable={() => handleDocStatusChange(emp, doc.key, 'kapsam_disi', doc)}
      />
      </>
    );
  };

  // Eksiklik şablonu kaydet
  const handleDeficiencySave = async () => {
    if (!selectedEmployeeForDoc || !selectedDocKey || selectedDeficiencies.length === 0) {
      toast.error('Lütfen en az bir eksiklik seçin');
      return;
    }
    
    try {
      const deficiencyText = selectedDeficiencies.join(', ');
      const updatedDocs = {
        ...selectedEmployeeForDoc.documents,
        [selectedDocKey]: 'eksik',
        [`${selectedDocKey}_note`]: deficiencyText,
        [`${selectedDocKey}_date`]: null,
        [`${selectedDocKey}_validity`]: null
      };
      
      await axios.patch(`${API}/employees/${selectedEmployeeForDoc.id}/documents`, { documents: updatedDocs });
      updateEmployeeState(selectedEmployeeForDoc.id, updatedDocs);
      toast.success('Eksiklik kaydedildi');
      
      setShowDeficiencyDialog(false);
      setSelectedEmployeeForDoc(null);
      setSelectedDocKey(null);
      setSelectedDeficiencies([]);
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Eksiklik toggle
  const toggleDeficiency = (templateText) => {
    setSelectedDeficiencies(prev => 
      prev.includes(templateText) 
        ? prev.filter(t => t !== templateText)
        : [...prev, templateText]
    );
  };

  // ==================== MYK İŞLEMLERİ ====================

  // MYK seçici aç
  const openMykSelector = (employee, status) => {
    setMykSelectorEmployee(employee);
    setMykSelectorStatus(status);
    setSelectedMykObj(null);
    setMykSearchQuery('');
    setMykValidityDate('');
    setShowDiplomaInput(false);
    setDiplomaText('');
    setSelectedDeficiencies([]);
    setMykRejectNote('');
    setShowMykDialog(true);
    closeUi();
  };

  // MYK listeden seç
  const handleMykListSelection = async () => {
    if (!mykSelectorEmployee || !selectedMykObj) return;
    
    if (mykSelectorStatus === 'onay' && !mykValidityDate) {
      toast.error('Geçerlilik tarihi giriniz');
      return;
    }
    if (mykSelectorStatus === 'onay' && validityError(mykValidityDate)) { toast.error(validityError(mykValidityDate)); return; }
    
    const mykDisplayName = selectedMykObj.code ? `${selectedMykObj.code} ${selectedMykObj.name}` : selectedMykObj.name;
    
    try {
      const updatedDocs = { ...mykSelectorEmployee.documents };
      updatedDocs.mesleki_yeterlilik = mykSelectorStatus;
      updatedDocs.mesleki_yeterlilik_myk = mykDisplayName;
      updatedDocs.mesleki_yeterlilik_type = 'list';
      
      if (mykSelectorStatus === 'eksik') {
        delete updatedDocs.mesleki_yeterlilik_date;
        delete updatedDocs.mesleki_yeterlilik_validity;
        updatedDocs.mesleki_yeterlilik_deficiencies = selectedDeficiencies;
        updatedDocs.mesleki_yeterlilik_note = [...selectedDeficiencies, mykRejectNote.trim()].filter(Boolean).join(', ') || `Seçili belge: ${mykDisplayName}`;
      } else if (mykSelectorStatus === 'onay') {
        updatedDocs.mesleki_yeterlilik_date = mykValidityDate;
        updatedDocs.mesleki_yeterlilik_validity = mykValidityDate;
        delete updatedDocs.mesleki_yeterlilik_note;
      }
      
      await axios.patch(`${API}/employees/${mykSelectorEmployee.id}/documents`, { documents: updatedDocs });
      updateEmployeeState(mykSelectorEmployee.id, updatedDocs);
      toast.success(`MYK belgesi ${mykSelectorStatus === 'onay' ? 'onaylandı' : 'eksik olarak işaretlendi'}`);
      closeMykDialog();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Diploma seç
  const handleDiplomaSelection = async () => {
    if (!mykSelectorEmployee || !diplomaText.trim()) {
      toast.error('Diploma adını giriniz');
      return;
    }
    
    try {
      const updatedDocs = { ...mykSelectorEmployee.documents };
      updatedDocs.mesleki_yeterlilik = mykSelectorStatus;
      updatedDocs.mesleki_yeterlilik_myk = diplomaText.trim();
      updatedDocs.mesleki_yeterlilik_type = 'diploma';
      
      if (mykSelectorStatus === 'eksik') {
        delete updatedDocs.mesleki_yeterlilik_date;
        delete updatedDocs.mesleki_yeterlilik_validity;
        updatedDocs.mesleki_yeterlilik_note = `Diploma: ${diplomaText.trim()}`;
      } else if (mykSelectorStatus === 'onay') {
        delete updatedDocs.mesleki_yeterlilik_date; // Diploma süresiz
        delete updatedDocs.mesleki_yeterlilik_validity; // Diploma süresiz
        delete updatedDocs.mesleki_yeterlilik_note;
      }
      
      await axios.patch(`${API}/employees/${mykSelectorEmployee.id}/documents`, { documents: updatedDocs });
      updateEmployeeState(mykSelectorEmployee.id, updatedDocs);
      toast.success(`Diploma ${mykSelectorStatus === 'onay' ? 'onaylandı' : 'eksik olarak işaretlendi'}`);
      closeMykDialog();
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  const closeMykDialog = () => {
    setShowMykDialog(false);
    setMykSelectorEmployee(null);
    setMykSelectorStatus(null);
    setSelectedMykObj(null);
    setMykSearchQuery('');
    setMykValidityDate('');
    setShowDiplomaInput(false);
    setDiplomaText('');
  };

  // ==================== TARİH GÜNCELLEME ====================

  const handleDocDateUpdate = async (employee, docKey, date) => {
    // Tarih doğrulama
    const validation = validateDates(employee, docKey, date);
    if (!validation.isValid) {
      toast.error(validation.message);
      return;
    }
    
    const updatedDocs = { ...employee.documents, [`${docKey}_date`]: date };
    
    try {
      await axios.patch(`${API}/employees/${employee.id}/documents`, { documents: updatedDocs });
      updateEmployeeState(employee.id, updatedDocs);
      toast.success('Tarih güncellendi');
    } catch (error) {
      toast.error('Güncelleme başarısız');
    }
  };

  // Tümünü seç/kaldır (pasif personeller hariç)
  const handleSelectAll = (checked) => {
    if (checked) {
      filteredEmployees.forEach(emp => {
        // Pasif personelleri atla
        if (emp.is_active === false) return;
        if (!selectedEmployees.find(e => e.id === emp.id)) {
          handleEmployeeSelect(emp, true);
        }
      });
    } else {
      filteredEmployees.forEach(emp => {
        if (selectedEmployees.find(e => e.id === emp.id)) {
          handleEmployeeSelect(emp, false);
        }
      });
    }
  };

  // Aktif personeller için tümü seçili mi kontrolü
  const activeEmployees = filteredEmployees.filter(emp => emp.is_active !== false);
  const allSelected = activeEmployees.length > 0 && activeEmployees.every(emp => selectedEmployees.find(e => e.id === emp.id));

  // Talep tarih aralığında belgesi dolacak belgeleri bul
  const getExpiringDocsInRange = (emp) => {
    if (!formData.end_date) return [];
    
    const endDate = (formData.end_date || '').slice(0, 10);
    const expiringDocs = [];
    
    // Kontrol edilecek belgeler ve validity alanları
    const docsToCheck = [
      { key: 'isg_egitimi', validityField: 'isg_egitimi_validity', label: 'İSG Eğitimi' },
      { key: 'saglik_raporu', validityField: 'saglik_raporu_validity', label: 'Sağlık Raporu' },
      { key: 'mesleki_yeterlilik', validityField: 'mesleki_yeterlilik_validity', altField: 'mesleki_yeterlilik_date', label: 'MYK' },
    ];
    
    docsToCheck.forEach(doc => {
      const status = emp.documents?.[doc.key];
      if (status === 'onay' || status === 'suresi_doldu') {
        const validityDate = emp.documents?.[doc.validityField] || (doc.altField ? emp.documents?.[doc.altField] : null);
        if (validityDate) {
          const validity = String(validityDate).slice(0, 10);
          if (validity < endDate) {
            expiringDocs.push({
              label: doc.label,
              date: validityDate,
              isExpired: validity < new Date().toLocaleDateString('sv-SE')
            });
          }
        }
      }
    });
    
    return expiringDocs;
  };

  // Genel durum badge'i
  const getOverallBadge = (emp) => {
    const overall = calculateOverallStatus(emp.documents, empDocKeys(emp));
    const expiringDocs = getExpiringDocsInRange(emp);
    const config = STATUS_CONFIG[overall.status] || STATUS_CONFIG['beklemede'];
    const Icon = config.icon;
    
    // Eğer talep aralığında dolacak belge varsa, özel gösterim
    if (expiringDocs.length > 0) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <Badge className="bg-orange-100 text-orange-700 gap-1 text-xs cursor-help">
              <AlertTriangle className="h-3 w-3" />
            </Badge>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <div className="text-xs space-y-1">
              <p className="font-medium text-orange-600">⚠️ Talep aralığında dolacak belgeler:</p>
              {expiringDocs.map((doc, i) => (
                <p key={i} className={doc.isExpired ? 'text-red-500' : 'text-orange-500'}>
                  • {doc.label}: {formatDate(doc.date)} {doc.isExpired ? '(Dolmuş!)' : ''}
                </p>
              ))}
            </div>
          </TooltipContent>
        </Tooltip>
      );
    }
    
    const OVERALL_LABELS = { onay: 'Onaylı', eksik: 'Eksik', suresi_doldu: 'Süresi Doldu', kapsam_disi: 'Kapsam Dışı' };
    const awaitingUpload = overall.status === 'beklemede' && !docKeys.some(k => emp.document_files?.[k]);
    const label = overall.status === 'beklemede' ? (awaitingUpload ? 'Yükleme Bekliyor' : 'Onay Bekliyor') : (OVERALL_LABELS[overall.status] || 'Onay Bekliyor');
    const BadgeIcon = awaitingUpload ? Clock : Icon;
    const cls = awaitingUpload ? 'bg-slate-100 text-slate-500' : `${config.bg} ${config.text}`;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="cursor-help" data-testid={`wizard-emp-overall-${emp.id}`} aria-label={label}>
            <Badge className={`${cls} gap-1 text-xs`}>
              <BadgeIcon className="h-3 w-3" />
            </Badge>
          </button>
        </TooltipTrigger>
        <TooltipContent side="right"><p className="text-xs font-medium">{label}</p></TooltipContent>
      </Tooltip>
    );
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="space-y-4">
      {sgkNewEmps.length > 0 && (
        <div className="rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="copy-sgk-warning">
          <p className="font-semibold">SGK Hizmet Listesi yeniden İSG kontrolüne sunulacak</p>
          <p className="text-xs">Kopyalanan talebe eklenen şu personel firmanın SGK Hizmet Listesi'nde henüz kontrol edilmedi: <b>{sgkNewEmps.map(e => `${e.name} ${e.surname}`).join(', ')}</b>. İSG listeyi bu kişiler için yeniden kontrol edecek.</p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <h3 className="font-medium">Personel Seçimi ve Belge Kontrolü</h3>
        {isReviewer && (
          <Button type="button" onClick={() => setReviewQueue({ list: pendingReview.map(x => x.id), idx: 0, opened: false })} disabled={!pendingReview.length} className="bg-sky-600 text-white shadow-md hover:bg-sky-700" data-testid="wizard-bulk-review-btn">
            <Eye className="h-4 w-4 mr-2" />Toplu Kontrol Et ({pendingReview.length})
          </Button>
        )}
        {isFirm && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPoolOpen(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800" data-testid="wizard-employee-pool-btn">
              <Users className="h-4 w-4 mr-1" />Personel Havuzu
            </Button>
            {canCreate('employees') && <Button variant="outline" size="sm" onClick={() => setNewEmployeeDialog(true)} className="hover:bg-slate-100 hover:text-slate-900" data-testid="wizard-new-employee-btn">
              <Plus className="h-4 w-4 mr-1" />Yeni Personel
            </Button>}
            {canCreate('employees') && <Button variant="outline" size="sm" onClick={() => setExcelOpen(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800" data-testid="wizard-excel-import-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" />Excel ile Ekle
            </Button>}
            <Button type="button" variant="outline" size="sm" disabled={!filteredEmployees.length} onClick={async () => exportEmployeesToExcel(await withFullTc(filteredEmployees), `personel_listesi_${new Date().toLocaleDateString('sv-SE')}.xlsx`)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 hover:text-emerald-800" data-testid="wizard-emp-export-excel">
              <Download className="h-4 w-4 mr-1" />Excel'e Aktar ({filteredEmployees.length})
            </Button>
          </div>
        )}
      </div>
      {isFirm && selectedEmployees.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border-2 border-sky-300 bg-sky-50 px-4 py-3" data-testid="wizard-bulk-upload-banner">
          <p className="flex items-center gap-2 text-sm font-semibold text-sky-900"><Upload className="h-5 w-5 text-sky-600" />Belgelerinizi toplu olarak tek seferde yüklemek için Toplu Yükle'ye basınız.</p>
          <Button type="button" onClick={() => openBulk({})} className="bg-sky-600 px-6 text-white shadow-md hover:bg-sky-700" data-testid="wizard-bulk-upload-top-btn">
            <Upload className="h-4 w-4 mr-2" />Toplu Yükle
          </Button>
        </div>
      )}
      {isFirm && (
        <ExcelImportDialog open={excelOpen} onOpenChange={setExcelOpen} companyId={sectionCompanyId || formData?.behalf_company_id || formData?.company_id} onBehalfOf={sectionKey === 'eq' ? null : formData?.on_behalf_of} onDone={handleExcelDone} />
      )}
      {bulkEmp && (
        <MatrixUploadDialog
          entities={employees.filter(e => selectedEmployees.some(s => s.id === e.id) && e.is_active !== false)}
          requiredDocs={requiredDocs} files={bulkFiles} setFiles={setBulkFiles} focusId={bulkEmp.id}
          idCard={advancedQr} onIdCard={patchEmployee}
          onClose={() => { setBulkEmp(null); setBulkFiles([]); }}
          onUploaded={(id, docKey, info) => updateEmployeeFile(id, docKey, info)}
          onDeleted={(id, docKey, res) => removeEmployeeFile(id, docKey, res)}
          onView={(emp, docKey, label) => setViewerDoc({ employee: emp, docKey, label })}
        />
      )}
      {isFirm && (
        <EmployeePoolDialog
          open={poolOpen} onOpenChange={setPoolOpen} employees={employees.filter(e => !e.is_archived)} selectedEmployees={selectedEmployees}
          requiredDocs={requiredDocs} formData={formData}
          onAdd={(list) => { list.forEach(emp => handleEmployeeSelect(emp, true)); toast.success(`${list.length} personel talebe eklendi`); }}
        />
      )}
      
      
      {/* Arama ve Filtre */}
      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input 
            placeholder="Personel ara..." 
            value={employeeSearch}
            onChange={(e) => { setEmployeeSearch(e.target.value); setPage(1); }}
            className="pl-10"
            data-testid="wizard-emp-search"
          />
        </div>
        <Select value={employeeStatusFilter} onValueChange={(v) => { setEmployeeStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue placeholder="Filtrele" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tümü</SelectItem>
            <SelectItem value="onay">Onaylı</SelectItem>
            <SelectItem value="eksik">Eksik</SelectItem>
            <SelectItem value="beklemede">Beklemede</SelectItem>
            <SelectItem value="suresi_doldu">Süresi Dolmuş</SelectItem>
            <SelectItem value="suresi_dolacak">⚠️ Talep Aralığında Dolacak</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isReviewer && (
        <div className="flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-3 py-2" data-testid="wizard-reviewer-docs-filter">
          <p className="text-xs text-amber-800">{showAllDocs ? 'Tüm belgeler gösteriliyor.' : 'Sadece onay bekleyen ve eksik belgeler gösteriliyor.'}</p>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => setShowAllDocs(v => !v)} data-testid="wizard-reviewer-toggle-all-docs">
            {showAllDocs ? 'Sadece onay bekleyen ve eksikleri göster' : 'Tüm belgeleri göster'}
          </Button>
        </div>
      )}

      {fixMode && (
        <div className="flex items-center justify-between rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
          <p className="text-xs text-rose-700">{showAllDocs ? 'Tüm belgeler gösteriliyor.' : 'Sadece eksik belgesi olan personeller ve eksik belgeler gösteriliyor.'}</p>
          <Button type="button" size="sm" variant="outline" className="h-7 text-xs bg-white" onClick={() => setShowAllDocs(v => !v)} data-testid="wizard-toggle-all-docs">
            {showAllDocs ? 'Sadece eksikleri göster' : 'Tüm belgeleri göster'}
          </Button>
        </div>
      )}

      {/* Tablo */}
      {employees.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-slate-50">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 mb-3">Bu firmada henüz personel yok</p>
          {isFirm && canCreate('employees') && <Button variant="outline" size="sm" onClick={() => setNewEmployeeDialog(true)} data-testid="wizard-empty-add-employee-btn">
            <Plus className="h-4 w-4 mr-1" />Personel Ekle
          </Button>}
        </div>
      ) : isFirm && selectedEmployees.length === 0 ? (
        <div className="text-center py-8 border rounded-lg bg-slate-50" data-testid="wizard-no-selected-employees">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500 mb-3">Henüz personel eklenmedi. Personel Havuzu'ndan seçin veya yeni personel ekleyin.</p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPoolOpen(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="wizard-empty-pool-btn">
              <Users className="h-4 w-4 mr-1" />Personel Havuzu
            </Button>
            {canCreate('employees') && <Button variant="outline" size="sm" onClick={() => setNewEmployeeDialog(true)} data-testid="wizard-empty-new-employee-btn">
              <Plus className="h-4 w-4 mr-1" />Yeni Personel
            </Button>}
            {canCreate('employees') && <Button variant="outline" size="sm" onClick={() => setExcelOpen(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="wizard-empty-excel-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1" />Excel ile Ekle
            </Button>}
          </div>
        </div>
      ) : filteredEmployees.length === 0 ? (
        <div className="text-center py-8">
          <Users className="h-12 w-12 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-500">{employeeSearch ? `"${employeeSearch}" için sonuç bulunamadı` : 'Bu filtreye uygun personel yok'}</p>
        </div>
      ) : (
        <div ref={fitZoom.outerRef} className="md:border rounded-lg overflow-x-auto lg:overflow-visible" data-testid="wizard-emp-table">
          <div style={fitZoom.zoom < 1 ? { zoom: fitZoom.zoom } : undefined} data-zoom={fitZoom.zoom.toFixed(2)} data-testid="wizard-emp-table-fit">
          {/* Tablo Başlığı - sayfa kaydırılırken üstte sabit */}
          <div className={`mgrid-head bg-slate-50 border-b sticky top-[113px] lg:top-[57px] z-[5] shadow-sm ${tableMinW}`} style={fitZoom.zoom < 1 ? { top: `${57 / fitZoom.zoom}px` } : undefined} data-testid="wizard-emp-table-header">
            <div style={gridStyle} className={`grid gap-1 items-center text-xs font-medium text-slate-600 px-2 py-2`}>
              <div className="flex items-center justify-center">
                <Checkbox checked={allSelected} onCheckedChange={handleSelectAll} disabled={!isFirm} data-testid="wizard-emp-select-all" />
              </div>
              <div className="text-center">Onay</div>
              <div>Ad Soyad</div>
              <div>T.C. / Pasaport No</div>
              <div>Görevi</div>
              {isReviewer && <div className="text-center" data-testid="wizard-sgk-col">SGK</div>}
              {/* Belge Sütunları */}
              {tableDocs.map(doc => (
                <div key={doc.key} className="text-center leading-tight" data-testid={`wizard-doc-col-${doc.key}`}>
                  <span>{doc.key === 'mesleki_yeterlilik' ? 'Mesleki Yeterlilik (MYK)' : doc.label}</span>
                  {(requiredDocs.includes(doc.key) || optionalDocs.includes(doc.key)) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button type="button" className={`ml-1 font-semibold cursor-help ${requiredDocs.includes(doc.key) ? 'text-amber-600' : 'text-slate-400'}`} data-testid={`wizard-doc-col-req-${doc.key}`}>
                          {requiredDocs.includes(doc.key) ? '(Z)' : '(O)'}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>{requiredDocs.includes(doc.key) ? 'Zorunlu' : 'Opsiyonel'}</TooltipContent>
                    </Tooltip>
                  )}
                  {isFirm && <div><PageLimitTag collection="employees" docKey={doc.key} /></div>}
                </div>
              ))}
              {advancedQr && (
                <div className="text-center leading-tight text-emerald-800" data-testid="wizard-id-card-col">
                  Kimlik Ön Yüz / Pasaport (QR) <span className="font-semibold text-amber-600">(Z)</span>
                </div>
              )}
              {isFirm && <div />}
            </div>
          </div>
          
          {/* Tablo Satırları */}
          <div className={`mgrid-wrap ${tableMinW}`}>
            {pg.rows.map((emp, rowIdx) => {
              const isSelected = selectedEmployees.some(e => e.id === emp.id);
              const sgkDate = emp.documents?.sgk_ise_giris_date;
              const isPassive = emp.is_active === false;
              const isBlocked = blockedIds.has(emp.id);
              
              return (
                <div 
                  key={emp.id} data-testid={`wizard-emp-row-${emp.id}`} data-blocked={isBlocked ? 'true' : undefined}
                  data-review-pending={isReviewer && Object.keys(emp.document_files || {}).some(k => (emp.documents?.[k] || 'beklemede') === 'beklemede') ? 'true' : undefined}
                  style={gridStyle} className={`mgrid-row grid gap-1 items-center px-2 py-2 border-b border-slate-200 last:border-b-0 transition-colors ${isBlocked ? 'bg-red-50 ring-2 ring-inset ring-red-500' : rowIdx % 2 ? 'bg-slate-100' : 'bg-white'} ${isPassive ? 'opacity-60' : 'hover:bg-sky-50'}`}
                >
                  {/* Checkbox */}
                  <div className="flex items-center justify-center gap-1.5" data-label="Seç">
                    <span className="min-w-[1.25rem] text-right text-[11px] font-semibold text-slate-500 tabular-nums" data-testid={`wizard-emp-row-no-${emp.id}`}>{pg.offset + rowIdx + 1}.</span>
                    <Checkbox 
                      checked={isSelected}
                      onCheckedChange={(checked) => handleEmployeeSelect(emp, checked)}
                      disabled={isPassive || !isFirm}
                      data-testid={`wizard-emp-select-${emp.id}`}
                    />
                  </div>
                  
                  {/* Genel Statü */}
                  <div className="flex justify-center" data-label="Onay">
                    {isPassive ? (
                      <Badge className="bg-slate-200 text-slate-600 text-[10px] px-1">
                        Pasif
                      </Badge>
                    ) : (
                      getOverallBadge(emp)
                    )}
                  </div>
                  
                  {/* Ad Soyad */}
                  <div data-label="Ad Soyad">
                    <p className={`font-medium text-sm truncate ${emp.is_blocked ? 'text-rose-600 line-through decoration-2 decoration-rose-600' : emp.is_archived || isPassive ? 'text-slate-400 line-through' : 'text-slate-800'}`}
                      title={emp.is_blocked ? 'Bloklanmıştır' : emp.is_archived ? 'Silinmiştir' : undefined} data-testid={`wizard-emp-name-${emp.id}`}>
                      {emp.name} {emp.surname}
                    </p>
                    {emp.is_blocked && <p className="text-[10px] font-semibold text-rose-600">Bloklanmıştır</p>}
                    {emp.is_archived && <p className="text-[10px] font-semibold text-slate-500">Silinmiştir</p>}
                    {emp.on_behalf_of && <p className="text-[10px] text-sky-700 truncate" data-testid={`wizard-emp-behalf-${emp.id}`}>{emp.on_behalf_of} adına</p>}
                  </div>
                  
                  {/* T.C. */}
                  <div data-label="T.C. / Pasaport">
                    <p className="text-xs text-slate-600 font-mono">{(isForeign(emp) ? emp.tc_kimlik_no : maskTcNo(emp.tc_kimlik_no)) || '-'}<PassportTag employee={emp} /></p>
                  </div>
                  
                  {/* Görevi */}
                  <div data-label="Görevi">
                    <p className="text-xs text-slate-600 truncate">{emp.position || '-'}</p>
                  </div>
                  {isReviewer && <div className="flex justify-center" data-label="SGK"><SgkMarkIcon mark={sgkMark(sgkData, emp)} empId={emp.id} /></div>}
                  {/* Belge Sütunları */}
                  {tableDocs.map(doc => {
                    const docStatus = emp.documents?.[doc.key] || 'beklemede';
                    const docDate = emp.documents?.[`${doc.key}_date`];
                    const noFileYet = docStatus === 'beklemede' && !emp.document_files?.[doc.key];
                    const config = noFileYet ? { ...STATUS_CONFIG['beklemede'], bg: 'bg-slate-100', text: 'text-slate-400', hoverBg: 'hover:bg-slate-200' } : (STATUS_CONFIG[docStatus] || STATUS_CONFIG['beklemede']);
                    const Icon = config.icon;
                    const isRequired = requiredDocs.includes(doc.key);
                    const isMyk = doc.key === 'mesleki_yeterlilik';
                    const canBeKapsamDisi = true;
                    const needsMinDate = (doc.key === 'isg_egitimi' || doc.key === 'saglik_raporu') && sgkDate;
                    
                    // Talep aralığında bu belge dolacak mı kontrol et
                    let isExpiringInRange = false;
                    let expiryDate = null;
                    if (formData.end_date && (docStatus === 'onay' || docStatus === 'suresi_doldu')) {
                      const endDate = (formData.end_date || '').slice(0, 10);
                      let validityDate = null;
                      if (doc.key === 'mesleki_yeterlilik') {
                        validityDate = emp.documents?.mesleki_yeterlilik_validity || emp.documents?.mesleki_yeterlilik_date;
                      } else {
                        validityDate = emp.documents?.[`${doc.key}_validity`];
                      }
                      if (validityDate) {
                        const validity = String(validityDate).slice(0, 10);
                        if (validity < endDate) {
                          isExpiringInRange = true;
                          expiryDate = validityDate;
                        }
                      }
                    }
                    
                    if ((isFirm && hideApproved && !needsAttention(emp, doc.key)) || (reviewerHide && !reviewerAttention(emp, doc.key))) return <div key={doc.key} />;
                    if (isFirm) return (
                      <div key={doc.key} className="flex justify-center" data-label={doc.label}>
                      <FirmDocCell
                        fixMode={fixMode}
                        note={emp.documents?.[`${doc.key}_note`]}
                        entityType="employees" entityId={emp.id} docKey={doc.key} docLabel={doc.label}
                        hasFile={!!emp.document_files?.[doc.key]} status={docStatus}
                        onUploaded={(info) => updateEmployeeFile(emp.id, doc.key, info)}
                        onDeleted={(res) => removeEmployeeFile(emp.id, doc.key, res)}
                        onView={() => setViewerDoc({ employee: emp, docKey: doc.key, label: doc.label })}
                        testPrefix={`wizard-doc-${emp.id}-${doc.key}`}
                        optional={requiredDocs.length > 0 && !requiredDocs.includes(doc.key)}
                      />
                      </div>
                    );

                    return (
                      <div key={doc.key} className="flex flex-col items-center justify-center" data-label={doc.label}>
                        <Popover 
                          open={openPopover === `${emp.id}-${doc.key}`}
                          onOpenChange={(open) => {
                            if (open && emp.document_files?.[doc.key]) {
                              setViewerDoc({ employee: emp, docKey: doc.key, label: doc.label });
                              return;
                            }
                            setOpenPopover(open ? `${emp.id}-${doc.key}` : null);
                            if (open) {
                              if (docDate) {
                                setTempDate(docDate);
                              } else if (needsMinDate) {
                                setTempDate(sgkDate);
                              } else {
                                setTempDate('');
                              }
                            }
                          }}
                        >
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <PopoverTrigger asChild>
                                <button 
                                  className={`w-8 h-8 rounded flex items-center justify-center relative ${isExpiringInRange ? 'bg-orange-100 text-orange-700 hover:bg-orange-200 ring-2 ring-orange-400' : `${config.bg} ${config.text} ${config.hoverBg}`} ${isRequired && !isExpiringInRange ? 'ring-1 ring-blue-300' : ''}`}
                                >
                                  {isExpiringInRange ? (
                                    <AlertTriangle className="h-4 w-4" />
                                  ) : (
                                    <Icon className="h-4 w-4" />
                                  )}
                                  {emp.document_files?.[doc.key] && (
                                    <Paperclip className="h-2.5 w-2.5 absolute -top-1 -right-1 text-blue-600" data-testid={`wizard-doc-file-indicator-${emp.id}-${doc.key}`} />
                                  )}
                                </button>
                              </PopoverTrigger>
                            </TooltipTrigger>
                            <TooltipContent side="top" className="max-w-xs">
                              <div className="text-xs space-y-1">
                                <p className="font-medium">{doc.label} · {firmDocStatus(docStatus, !!emp.document_files?.[doc.key]).label}</p>
                                {/* Talep aralığında dolacak uyarısı */}
                                {isExpiringInRange && (
                                  <p className="text-orange-500 font-medium">⚠️ Talep aralığında dolacak: {formatDate(expiryDate)}</p>
                                )}
                                {/* MYK için seçilen belge adı ve geçerlilik */}
                                {doc.key === 'mesleki_yeterlilik' && emp.documents?.mesleki_yeterlilik_myk && (
                                  <>
                                    <p className="text-blue-400">📜 {emp.documents.mesleki_yeterlilik_myk}</p>
                                    {(emp.documents?.mesleki_yeterlilik_validity || emp.documents?.mesleki_yeterlilik_date) && (
                                      <p className="text-purple-400">📅 Geçerlilik: {formatDate(emp.documents.mesleki_yeterlilik_validity || emp.documents.mesleki_yeterlilik_date)}</p>
                                    )}
                                  </>
                                )}
                                {/* Diğer belgeler için geçerlilik bitiş tarihi */}
                                {doc.key !== 'mesleki_yeterlilik' && emp.documents?.[`${doc.key}_validity`] && (
                                  <p className="text-purple-400">📅 Geçerlilik: {formatDate(emp.documents[`${doc.key}_validity`])}</p>
                                )}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                          <PopoverContent className="w-80 p-3" align="center">
                            <p className="font-medium text-sm mb-1 text-slate-700">{doc.label}</p>

                            {/* Belge dosyası: firma yükler / siler, inceleyen görüntüleyip onaylar */}
                            <div className="mb-2">
                              <FirmDocStatusBadge status={docStatus} hasFile={!!emp.document_files?.[doc.key]} testId={`wizard-doc-status-${emp.id}-${doc.key}`} />
                            </div>
                            <div className="mb-3">
                              {isFirm ? (
                                <FirmDocFileActions
                                  entityType="employees" entityId={emp.id} docKey={doc.key} docLabel={doc.label}
                                  hasFile={!!emp.document_files?.[doc.key]} status={docStatus}
                                  onUploaded={(info) => updateEmployeeFile(emp.id, doc.key, info)}
                                  onDeleted={(res) => removeEmployeeFile(emp.id, doc.key, res)}
                                  onView={() => { setOpenPopover(null); setViewerDoc({ employee: emp, docKey: doc.key, label: doc.label }); }}
                                  testPrefix={`wizard-doc-${emp.id}-${doc.key}`}
                                />
                              ) : emp.document_files?.[doc.key] && (
                                <button
                                  type="button"
                                  onClick={() => { setOpenPopover(null); setViewerDoc({ employee: emp, docKey: doc.key, label: doc.label }); }}
                                  className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded border ${docStatus === 'onay' ? 'border-rose-600 bg-rose-600 text-white hover:bg-rose-700' : 'border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                                  data-testid={`wizard-doc-view-btn-${emp.id}-${doc.key}`}
                                >
                                  <Eye className="h-3.5 w-3.5" />{docStatus === 'onay' ? 'Görüntüle veya Eksik/Hatalı Bildir' : 'Görüntüle ve Onayla'}
                                </button>
                              )}
                            </div>
                            
                            {/* MYK için seçilen belge adı ve geçerlilik tarihi */}
                            {doc.key === 'mesleki_yeterlilik' && emp.documents?.mesleki_yeterlilik_myk && (
                              <div className="text-xs text-indigo-600 mb-2 bg-indigo-50 px-2 py-1.5 rounded space-y-1">
                                <p>📜 {emp.documents.mesleki_yeterlilik_myk}</p>
                                {(emp.documents?.mesleki_yeterlilik_validity || emp.documents?.mesleki_yeterlilik_date) && (
                                  <p className="text-purple-600">📅 Geçerlilik: {formatDate(emp.documents.mesleki_yeterlilik_validity || emp.documents.mesleki_yeterlilik_date)}</p>
                                )}
                              </div>
                            )}
                            
                            {/* İSG Eğitimi için Uzman bilgisi */}
                            {doc.key === 'isg_egitimi' && firmRef.isg_katip_uzman_name && (
                              <p className="text-xs text-blue-600 mb-2 bg-blue-50 px-2 py-1 rounded">
                                👤 Uzman: {firmRef.isg_katip_uzman_name}
                              </p>
                            )}
                            
                            {/* İSG Eğitimi ve Sağlık Raporu için Hekim bilgisi */}
                            {(doc.key === 'saglik_raporu' || doc.key === 'isg_egitimi') && firmRef.isg_katip_hekim_name && (
                              <p className="text-xs text-green-600 mb-2 bg-green-50 px-2 py-1 rounded">
                                👨‍⚕️ Hekim: {firmRef.isg_katip_hekim_name}
                              </p>
                            )}
                            
                            {/* Tehlike sınıfına göre geçerlilik süresi bilgisi */}
                            {(doc.key === 'isg_egitimi' || doc.key === 'saglik_raporu') && selectedCompany?.hazard_class && (
                              <p className="text-xs text-purple-600 mb-2 bg-purple-50 px-2 py-1 rounded">
                                ⏱️ Geçerlilik: {getValidityPeriodText(doc.key, selectedCompany.hazard_class)} ({selectedCompany.hazard_class === 'az_tehlikeli' ? 'Az Tehlikeli' : selectedCompany.hazard_class === 'cok_tehlikeli' ? 'Çok Tehlikeli' : 'Tehlikeli'})
                              </p>
                            )}
                            
                            {/* Durum Seçimi */}
                            <div className="space-y-1 mb-3">
                              <p className="text-[11px] text-slate-500 bg-slate-50 rounded px-2 py-1.5 mb-1" data-testid={`wizard-doc-approve-hint-${emp.id}-${doc.key}`}>
                                {isFirm
                                  ? (emp.document_files?.[doc.key] ? 'Belge İSG Uzmanı tarafından incelenecek' : 'Belge dosyasını yükleyin')
                                  : (emp.document_files?.[doc.key] ? 'Onay / Red için belgeyi görüntüleyin' : 'Firma henüz belge yüklemedi')}
                              </p>
                              {emp.documents?.[`${doc.key}_note`] && emp.documents?.[doc.key] === 'eksik' && (
                                <p className="text-[11px] text-rose-600 px-2" data-testid={`wizard-doc-reject-note-${emp.id}-${doc.key}`}>Red nedeni: {emp.documents[`${doc.key}_note`]}</p>
                              )}
                              {isReviewer && (<>
                              <button
                                onClick={() => handleDocStatusChange(emp, doc.key, 'beklemede', doc)}
                                className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-amber-50 text-amber-700"
                              >
                                <Clock className="h-3.5 w-3.5" />
                                Beklemede
                              </button>
                              {canBeKapsamDisi && (
                                <button
                                  onClick={() => handleDocStatusChange(emp, doc.key, 'kapsam_disi', doc)}
                                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs rounded hover:bg-slate-50 text-slate-500"
                                >
                                  <MinusCircle className="h-3.5 w-3.5" />
                                  Kapsam Dışı
                                </button>
                              )}
                              </>)}
                            </div>
                            
                          </PopoverContent>
                        </Popover>
                        {isReviewer && <div className="mt-0.5"><PageCountBadge info={emp.document_files?.[doc.key]} testId={`wizard-doc-pages-${emp.id}-${doc.key}`} /></div>}
                      </div>
                    );
                  })}
                  {advancedQr && (
                    <div className="flex flex-col items-center justify-center" data-label="Kimlik (QR)" data-testid={`wizard-id-card-cell-${emp.id}`}>
                      {isSelected ? <IdCardChip employee={emp} canEdit={isFirm} canDecide={isReviewer} onChanged={(patch) => patchEmployee(emp.id, patch)} /> : <span className="text-[11px] text-slate-300">—</span>}
                    </div>
                  )}
                  {isFirm && (
                    <div className="flex justify-end pr-1">
                      {isSelected && (
                        <button type="button" onClick={() => { handleEmployeeSelect(emp, false); toast.success(`${emp.name} ${emp.surname} talepten çıkarıldı`, { duration: 6000, action: { label: 'Geri Al', onClick: () => handleEmployeeSelect(emp, true) } }); }}
                          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-rose-300 bg-white px-2.5 py-1 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-50 hover:text-rose-700" data-testid={`wizard-emp-remove-${emp.id}`}>
                          <X className="h-3.5 w-3.5" />Talepten Çıkar
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </div>
        </div>
      )}
      {filteredEmployees.length > 0 && (
        <TablePagination total={filteredEmployees.length} page={pg.cur} totalPages={pg.totalPages} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} marks={pageMarks} prefix="wizard-emp" />
      )}
      
      {/* Seçili Sayısı */}
      <div className="flex items-center justify-between pt-2 border-t">
        <p className="text-sm text-slate-500">
          <span className="font-medium text-emerald-600">{selectedEmployees.length}</span> personel seçildi
        </p>
        {isFirm && selectedEmployees.length > 0 && (
          <Button variant="ghost" size="sm" onClick={() => selectedEmployees.forEach(e => handleEmployeeSelect(e, false))} data-testid="wizard-emp-clear-selection">
            Seçimi Temizle
          </Button>
        )}
      </div>

      {/* Eksiklik Şablonu Dialog */}
      <Dialog open={showDeficiencyDialog} onOpenChange={setShowDeficiencyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Eksiklik Seçin - {selectedDocKey && EMPLOYEE_DOCS.find(d => d.key === selectedDocKey)?.label}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3 max-h-[400px] overflow-y-auto">
            {selectedDocKey && getTemplatesForDoc(selectedDocKey).map((template, i) => (
              <div key={i} className="flex items-start gap-3">
                <Checkbox
                  id={`def-${i}`}
                  checked={selectedDeficiencies.includes(template.template_text)}
                  onCheckedChange={() => toggleDeficiency(template.template_text)}
                />
                <Label htmlFor={`def-${i}`} className="text-sm cursor-pointer">
                  {template.template_text}
                </Label>
              </div>
            ))}
            {selectedDocKey && getTemplatesForDoc(selectedDocKey).length === 0 && (
              <p className="text-slate-500 text-sm">Bu belge için eksiklik şablonu tanımlanmamış.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeficiencyDialog(false)}>İptal</Button>
            <Button 
              onClick={handleDeficiencySave}
              disabled={selectedDeficiencies.length === 0}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Eksik Olarak Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MYK Seçim Dialog */}
      <Dialog open={showMykDialog} onOpenChange={setShowMykDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5 text-blue-600" />
              MYK / Diploma Seçimi
              {mykSelectorStatus === 'onay' && <Badge className="bg-emerald-100 text-emerald-700">Onay</Badge>}
              {mykSelectorStatus === 'eksik' && <Badge className="bg-rose-100 text-rose-700">Eksik</Badge>}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Seçim Butonları */}
            <div className="flex gap-2">
              <Button 
                variant={!showDiplomaInput ? "default" : "outline"} 
                size="sm"
                onClick={() => setShowDiplomaInput(false)}
              >
                MYK Listesinden Seç
              </Button>
              <Button 
                variant={showDiplomaInput ? "default" : "outline"} 
                size="sm"
                onClick={() => setShowDiplomaInput(true)}
              >
                Diploma Gir
              </Button>
            </div>
            
            {!showDiplomaInput ? (
              <>
                {/* MYK Arama */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="MYK ara (kod, ad veya açıklama)..."
                    value={mykSearchQuery}
                    onChange={(e) => setMykSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                
                {/* MYK Listesi */}
                <div className="border rounded-lg max-h-60 overflow-y-auto">
                  {filteredMykList.length === 0 ? (
                    <p className="p-4 text-center text-slate-500">MYK bulunamadı</p>
                  ) : (
                    filteredMykList.map((myk, i) => {
                      // Benzersiz tanımlayıcı: code + description (seviye bilgisi)
                      const mykUniqueId = `${myk.code || ''}-${myk.description || ''}-${i}`;
                      const isSelected = selectedMykObj && selectedMykObj.code === myk.code && selectedMykObj.description === myk.description;
                      
                      return (
                        <div 
                          key={mykUniqueId}
                          onClick={() => setSelectedMykObj(isSelected ? null : myk)}
                          className={`p-3 border-b last:border-b-0 cursor-pointer hover:bg-slate-50 ${isSelected ? 'bg-blue-50 border-blue-200' : ''}`}
                        >
                          <div className="flex items-start gap-2">
                            <Checkbox checked={isSelected} readOnly />
                            <div>
                              <p className="font-medium text-sm">
                                {myk.code && <span className="text-blue-600 mr-2">{myk.code}</span>}
                                {myk.name}
                              </p>
                              {myk.description && (
                                <p className="text-xs text-slate-500 mt-0.5">{myk.description}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                
                {/* Eksik/Hatalı: MYK seçimi + eksiklik şablonları */}
                {mykSelectorStatus === 'eksik' && (
                  <div className="space-y-2" data-testid="myk-deficiency-field">
                    <Label>Eksiklikler</Label>
                    {getTemplatesForDoc('mesleki_yeterlilik').length > 0 && (
                      <div className="max-h-40 space-y-1 overflow-y-auto rounded border bg-white p-2" data-testid="myk-deficiency-templates">
                        {getTemplatesForDoc('mesleki_yeterlilik').map((t, i) => (
                          <label key={t.id || i} className="flex cursor-pointer items-start gap-2 text-xs">
                            <Checkbox checked={selectedDeficiencies.includes(t.template_text)} onCheckedChange={() => toggleDeficiency(t.template_text)} data-testid={`myk-deficiency-template-${i}`} />
                            <span className="text-slate-600">{t.template_text}</span>
                          </label>
                        ))}
                      </div>
                    )}
                    <Textarea value={mykRejectNote} onChange={(e) => setMykRejectNote(e.target.value)} placeholder="Ek açıklama (isteğe bağlı)..." rows={2} className="text-xs" data-testid="myk-deficiency-note" />
                  </div>
                )}
                {/* Geçerlilik Tarihi (Sadece Onay için) - her zaman görünür */}
                {mykSelectorStatus === 'onay' && (
                  <div className="space-y-2" data-testid="myk-validity-date-field">
                    <Label>Geçerlilik Tarihi *</Label>
                    <Input
                      type="date"
                      value={mykValidityDate}
                      onChange={(e) => setMykValidityDate(e.target.value)}
                    />
                    {validityError(mykValidityDate) && <p className="rounded border-2 border-rose-400 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-700" data-testid="myk-validity-error">{validityError(mykValidityDate)}</p>}
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Diploma Adı *</Label>
                  <Input
                    placeholder="Örn: Elektrik Mühendisliği Diploması"
                    value={diplomaText}
                    onChange={(e) => setDiplomaText(e.target.value)}
                  />
                  <p className="text-xs text-slate-500">Diploma süresiz olarak kaydedilir.</p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={closeMykDialog}>İptal</Button>
            {!showDiplomaInput ? (
              <Button 
                onClick={handleMykListSelection}
                disabled={!selectedMykObj || (mykSelectorStatus === 'onay' && (!mykValidityDate || !!validityError(mykValidityDate))) || (mykSelectorStatus === 'eksik' && !selectedDeficiencies.length && !mykRejectNote.trim())}
                className={mykSelectorStatus === 'onay' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
              >
                {mykSelectorStatus === 'onay' ? 'Onayla' : 'Eksik Olarak Kaydet'}
              </Button>
            ) : (
              <Button 
                onClick={handleDiplomaSelection}
                disabled={!diplomaText.trim()}
                className={mykSelectorStatus === 'onay' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-rose-600 hover:bg-rose-700'}
              >
                {mykSelectorStatus === 'onay' ? 'Onayla' : 'Eksik Olarak Kaydet'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DocumentViewerDialog
        open={!!viewerDoc}
        onOpenChange={(open) => { if (!open) { setViewerDoc(null); setReviewQueue(null); } }}
        subtitle={viewerDoc ? `${viewerDoc.employee.name} ${viewerDoc.employee.surname}` : ''}
        entity={viewerDoc ? (employees.find(e => e.id === viewerDoc.employee.id) || viewerDoc.employee) : null}
        docKey={viewerDoc?.docKey}
        docLabel={viewerDoc?.label}
        mode="latest"
        compact={isFirm}
        onReplaced={isReviewer && viewerDoc ? (info) => updateEmployeeFile(viewerDoc.employee.id, viewerDoc.docKey, info) : undefined}
        decisionPanel={viewerDoc && isReviewer && renderEmployeeDocPanel()}
        extraInfo={viewerDoc && (
          <div className="space-y-2">
            {reviewQueue && (
              <div className="space-y-2 rounded-lg border-2 border-sky-300 bg-sky-50 px-3 py-2" data-testid="review-queue-bar">
                <p className="whitespace-nowrap text-xs font-semibold text-sky-900" data-testid="review-queue-progress">Toplu kontrol · {reviewQueue.idx + 1} / {reviewQueue.list.length}</p>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button type="button" size="sm" variant="outline" className="h-8 bg-white text-xs" disabled={reviewQueue.idx === 0} onClick={() => goQueue(reviewQueue.idx - 1)} data-testid="review-queue-prev"><ChevronLeft className="h-4 w-4 mr-1" />Önceki</Button>
                  <Button type="button" size="sm" variant="outline" className="h-8 bg-white text-xs" disabled={reviewQueue.idx >= reviewQueue.list.length - 1} onClick={() => goQueue(reviewQueue.idx + 1)} data-testid="review-queue-next">Sonraki<ChevronRight className="h-4 w-4 ml-1" /></Button>
                  <Button type="button" size="sm" variant="outline" className="h-8 bg-white text-xs" onClick={() => setViewerDoc(null)} data-testid="review-queue-skip">Atla</Button>
                  <Button type="button" size="sm" variant="outline" className="h-8 bg-white text-xs text-rose-600" onClick={() => { setReviewQueue(null); setViewerDoc(null); }} data-testid="review-queue-stop">Bitir</Button>
                </div>
              </div>
            )}
            <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm" data-testid="viewer-validity">
              {EMPLOYEE_DOCS.find(d => d.key === viewerDoc.docKey)?.hasValidity ? (
                <><span className="text-purple-700">Geçerlilik Tarihi: </span><span className={`font-medium ${pickedDate && validityError(pickedValidity) ? 'text-rose-700' : 'text-purple-900'}`} data-testid="viewer-validity-value">{(pickedDate ? pickedValidity : viewerValidity) ? formatDate(pickedDate ? pickedValidity : viewerValidity) : '-'}</span></>
              ) : (
                <><span className="text-purple-700">Tarih: </span><span className="font-medium text-purple-900" data-testid="viewer-date-value">{(pickedDate || viewerDocs[`${viewerDoc.docKey}_date`]) ? formatDate(pickedDate || viewerDocs[`${viewerDoc.docKey}_date`]) : '-'}</span></>
              )}
            </div>
            {['eksik', 'suresi_doldu'].includes(viewerDocs[viewerDoc.docKey]) && (
              <DeficiencyList docLabel={viewerDoc.label} note={viewerDocs[`${viewerDoc.docKey}_note`]} />
            )}
            {isFirm && ['eksik', 'suresi_doldu'].includes(viewerDocs[viewerDoc.docKey]) && (
              <NewVersionUpload
                entityType="employees"
                entityId={viewerDoc.employee.id} docKey={viewerDoc.docKey} note={viewerDocs[`${viewerDoc.docKey}_note`]}
                onUploaded={(info) => { updateEmployeeFile(viewerDoc.employee.id, viewerDoc.docKey, info); setViewerDoc(null); }}
              />
            )}
            {viewerPersons && (
              <div className="space-y-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm" data-testid="viewer-isg-person">
                {viewerPersons.map(([label, name]) => (
                  <p key={label}><span className="text-emerald-700">{label}: </span><span className="font-medium text-emerald-900">{name || 'Henüz girilmedi (Bölüm 2)'}</span></p>
                ))}
              </div>
            )}
          </div>
        )}
      />
    </div>
    </TooltipProvider>
  );
};

export default WizardStep3Combined;
